import * as Parse from "parse";

interface RequestLog {
  endpoint: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  type: "main" | "background" | "prefetch";
  context?: string;
  payload?: any;
  success?: boolean;
  error?: any;
  restaurantIds?: string[];
}

// Store active requests to track concurrent requests
const activeRequests = new Map<string, RequestLog[]>();

/**
 * Wraps Parse.Cloud.run with comprehensive logging and timing
 */
export const loggedCloudRun = async (
  endpoint: string,
  payload: any,
  options: {
    type?: "main" | "background" | "prefetch";
    context?: string;
  } = {}
): Promise<any> => {
  const { type = "main", context = "unknown" } = options;
  const startTime = performance.now();
  const timestamp = new Date().toISOString();

  // Log request start
  const log: RequestLog = {
    endpoint,
    startTime,
    type,
    context,
    payload: payload ? { ...payload } : undefined,
    restaurantIds: payload?.restaurantIds || [],
  };

  // Track active requests
  if (!activeRequests.has(endpoint)) {
    activeRequests.set(endpoint, []);
  }
  activeRequests.get(endpoint)!.push(log);

  const activeCount = activeRequests.get(endpoint)!.length;
  const totalActive = Array.from(activeRequests.values()).flat().length;

  console.log(
    `🚀 [API-${type.toUpperCase()}] ${endpoint}`,
    `| Context: ${context}`,
    `| Active: ${activeCount} for this endpoint, ${totalActive} total`,
    `| Time: ${timestamp}`,
    `| Payload:`,
    payload
  );

  // Log warning if too many concurrent requests at start
  if (totalActive > 10) {
    console.warn(
      `⚠️ [API] High concurrency detected: ${totalActive} active requests`,
      `| This may affect performance`,
      `| New request: ${endpoint} (${type})`
    );
  }

  try {
    const result = await Parse.Cloud.run(endpoint, payload);
    const endTime = performance.now();
    const duration = endTime - startTime;

    log.endTime = endTime;
    log.duration = duration;
    log.success = result?.success !== false;

    // Remove from active requests
    const activeList = activeRequests.get(endpoint);
    if (activeList) {
      const index = activeList.indexOf(log);
      if (index > -1) activeList.splice(index, 1);
    }

    const status = log.success ? "✅" : "⚠️";
    const durationColor = duration > 3000 ? "🔴" : duration > 1500 ? "🟡" : "🟢";

    console.log(
      `${status} [API-${type.toUpperCase()}] ${endpoint} completed`,
      `| Duration: ${durationColor} ${duration.toFixed(2)}ms`,
      `| Context: ${context}`,
      `| Success: ${log.success}`,
      `| Remaining active: ${Array.from(activeRequests.values()).flat().length}`
    );

    // Log warning for slow requests
    if (duration > 3000) {
      console.warn(
        `⚠️ [API] Slow request detected: ${endpoint} took ${duration.toFixed(2)}ms (>3s)`,
        `| Context: ${context}`,
        `| Type: ${type}`
      );
    }

    // Log remaining active requests after completion
    const remainingActive = Array.from(activeRequests.values()).flat().length;
    if (remainingActive > 10) {
      console.warn(
        `⚠️ [API] Still high concurrency: ${remainingActive} active requests remaining`,
        `| This may affect performance`
      );
    }

    return result;
  } catch (error: any) {
    const endTime = performance.now();
    const duration = endTime - startTime;

    log.endTime = endTime;
    log.duration = duration;
    log.success = false;
    log.error = error;

    // Remove from active requests
    const activeList = activeRequests.get(endpoint);
    if (activeList) {
      const index = activeList.indexOf(log);
      if (index > -1) activeList.splice(index, 1);
    }

    console.error(
      `❌ [API-${type.toUpperCase()}] ${endpoint} failed`,
      `| Duration: ${duration.toFixed(2)}ms`,
      `| Context: ${context}`,
      `| Error:`,
      error
    );

    throw error;
  }
};

/**
 * Get summary of all active requests
 */
export const getActiveRequestsSummary = (): {
  total: number;
  byEndpoint: Record<string, number>;
  byType: Record<string, number>;
} => {
  const allActive = Array.from(activeRequests.values()).flat();
  const byEndpoint: Record<string, number> = {};
  const byType: Record<string, number> = {};

  allActive.forEach((req) => {
    byEndpoint[req.endpoint] = (byEndpoint[req.endpoint] || 0) + 1;
    byType[req.type] = (byType[req.type] || 0) + 1;
  });

  return {
    total: allActive.length,
    byEndpoint,
    byType,
  };
};

/**
 * Log summary of active requests (useful for debugging)
 */
export const logActiveRequestsSummary = () => {
  const summary = getActiveRequestsSummary();
  console.log("📊 [API] Active Requests Summary:", summary);
};

/**
 * Check if any main requests are currently active
 */
export const hasActiveMainRequests = (): boolean => {
  const allActive = Array.from(activeRequests.values()).flat();
  return allActive.some((req) => req.type === "main");
};

